import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductionUnitApi from "../../services/ProductionUnitApi";
import PlantApi from "../../services/PlantApi";
import { useToast } from "../../context/ToastContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreateProductionUnitForm: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [plantId, setPlantId] = useState("");
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchPlants();
  }, []);

  const fetchPlants = async (): Promise<void> => {
    try {
      const response = await PlantApi.GetAllPlants();
      const options = (response.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar plantas: ", error);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!name || !plantId) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Duplicity check
      const unitsResponse = await ProductionUnitApi.GetAllProductUnit();
      const units = unitsResponse.data || [];
      const exists = units.some((u: any) => u.name.toLowerCase() === name.toLowerCase());

      if (exists) {
        displayToast("error", translateMessages("duplicateProductionUnitError"), "", false, 3000);
        return;
      }

      await ProductionUnitApi.CreateProductUnit(plantId, name);
      displayToast("success", translateMessages("registerSuccess"), "", false, 3000);
      navigate("/production-units");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("newProductionUnit")}
      icon="add"
      onSubmit={handleSave}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setPlantId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/production-units")}
      />
    </FormCard>
  );
};

export default CreateProductionUnitForm;
